import { makeAutoObservable, observable } from "mobx";
import Storage from "utils/storage";
import { get_symbol, get_plate } from "api/v4/market";
import { get_marketConfig } from "api/old/xt-lever";
import { get_marketTips } from "api/old/xt-app";
import { post_getEtfByTradeMarket } from "api/old/xt-redemption";
import { get_fapiSymbolList } from "api/old/futures";
import user from "./user";

export interface SymbolProps extends ObjAny {
  symbol: string; //市场名称
  quantityPrecision: number; //卖方币市场精度
  pricePrecision: number; //买方币市场精度
  plates: number[]; //板块列表id
  state: string; //交易对状态[ONLINE=上线的;OFFLINE=下线的,DELISTED=退市]
  nextState: null | string; //下一个状态
  nextStateTime: null | number; //下一个状态时间
  displayLevel: string; //展示级别,[FULL=完全展示,SEARCH=搜索展示,DIRECT=直达展示,NONE=不展示]
  tradingEnabled: boolean; //是否启动交易(暂停交易)
  displayWeight: number; //展示权重，越大越靠前
  depthMergePrecision: number; //深度合并精度
}

export interface LeverSymbolProps extends ObjAny {
  marketName: string; //市场名称
  maxLeverage: number; //最大杠杆倍数
  liquidationRate: string; //市场风险率
  tipsRate: string; //预警风险率
  dailyInterestRate: string;
}

export interface FuturesSymbolProps extends ObjAny {
  symbol: string; //交易对名称
}

export interface EtfConfigDataProps {
  id: number;
  multiple: number;
  dealPair: string;
}

interface EtfConfigProps {
  data: EtfConfigDataProps[];
}

interface TipsProps extends ObjAny {
  tipsType: number;
  content: string;
  link?: string;
}

interface PlateProps {
  id: string;
  plate: string;
}

export enum TypeEnum {
  spot = "SPOT", //现货
  lever = "LEVER", //杠杆
}

export interface IEtfDealPairItemProps {
  coinVol: number;
  dealPairName: string;
  name: string;
  high: number;
  low: number;
  moneyVol: number;
  multiple: number;
  price: number;
  rate: number;
}
interface StateProps {
  name: string;
  type: TypeEnum;
  tips: ObjT<TipsProps>;
  symbols: WithUndefined<SymbolProps[]>;
  leverConfigAry: WithUndefined<LeverSymbolProps[]>;
  etfConfig: WithUndefined<ObjT<EtfConfigProps>>;
  plateList: PlateProps[];
  netWorth: string;
  etfDealPairListData: IEtfDealPairItemProps[];
  isFuturesUsdtOpen: WithUndefined<boolean>;
  isFuturesCoinOpen: WithUndefined<boolean>;
  futuresUsdtTransferList: WithUndefined<string[]>;
  futuresCoinTransferList: WithUndefined<string[]>;
  futuresUsdtConfigAry: WithUndefined<FuturesSymbolProps[]>;
}

let loading_getMarketConfig = false;
let loading_getCfdMarketConfig = false;

const market = makeAutoObservable(
  {
    name: "" as StateProps["name"], //当前市场名称
    type: TypeEnum.spot as StateProps["type"], //当前市场类型，spot=现货，lever=杠杆
    tips: {} as ObjT<TipsProps>, //所有市场风险提示信息
    symbols: undefined as StateProps["symbols"], //所有市场配置信息，数组
    leverConfigAry: undefined as StateProps["leverConfigAry"], //杠杆所有市场配置信息，数组
    etfConfig: undefined as StateProps["etfConfig"], //现货市场对应的etf市场对象
    plateList: [] as StateProps["plateList"], //板块列表
    netWorth: "" as StateProps["netWorth"], //etf当前市场，通证净值
    etfDealPairListData: [] as StateProps["etfDealPairListData"], //etf交易对数据
    isFuturesUsdtOpen: undefined as StateProps["isFuturesUsdtOpen"], //判断U本位合约是否开通
    isFuturesCoinOpen: undefined as StateProps["isFuturesCoinOpen"], //判断币本位合约是否开通
    futuresUsdtTransferList: undefined as StateProps["futuresUsdtTransferList"], //U本位合约可划转币种列表
    futuresCoinTransferList: undefined as StateProps["futuresCoinTransferList"], //币本位合约可划转币种列表
    futuresUsdtConfigAry: undefined as StateProps["futuresUsdtConfigAry"], //U本位合约所有市场配置信息，数组

    get config(): WithUndefined<ObjT<SymbolProps>> {
      if (!this.symbols) return;
      const doc: ObjT<SymbolProps> = {};
      this.symbols.map((obj: any) => (doc[obj.symbol] = obj));
      return doc;
    }, //所有市场配置信息，对象
    get currentConfig(): Partial<SymbolProps> {
      if (!this.name || !this.config) return {};
      return this.config[this.name] || {};
    }, //当前市场配置信息
    get leverConfigObj(): WithUndefined<ObjT<LeverSymbolProps>> {
      if(!user.authInfo || !user.authInfo.country || user.authInfo.country.code == 971)return;
      if (!this.leverConfigAry) return;
      const doc: ObjT<LeverSymbolProps> = {};
      this.leverConfigAry.map((obj: any) => (doc[obj.marketName] = obj));
      return doc;
    }, //杠杆所有市场配置信息，对象
    get currentLeverConfig(): Partial<LeverSymbolProps> {
      if (!this.leverConfigObj || !this.leverConfigObj[this.name]) return {};
      return this.leverConfigObj[this.name];
    }, //当前杠杆市场配置信息
    get isLever(): boolean {
      return this.type === TypeEnum.lever;
    }, //是否是杠杆市场
    get isEtf(): boolean {
      // if (!this.name || !this.config) return false;
      // const cfg = this.config[this.name];
      // if (!cfg) return false;
      // return this.isEtfSymbolFn(cfg);
      return false;
    }, //当前市场是否是 etf 市场

    updateState(payload: Partial<StateProps>) {
      for (const va in payload) {
        this[va] = payload[va];
      }
    },
    updateStateOnce(payload: Partial<StateProps>) {
      for (const va in payload) {
        if (!this[va]) this[va] = payload[va];
      }
    },
    formatName(symbol: string): string {
      // !symbol && (symbol = this.name);
      return symbol.split("_").join("/").toUpperCase();
    }, //对市场名称进行格式化，btc_usdt -> BTC/USDT
    formatCfdName(symbol: string): string {
      if (!symbol) return "";
      // 去掉 _cfd 后缀（不区分大小写）
      const symbolWithoutCfd = symbol.replace(/_cfd$/i, "");
      // 使用 formatName 格式化
      return this.formatName(symbolWithoutCfd);
    }, //对CFD市场名称进行格式化，btc_usdt_cfd -> BTC/USDT
    //
    getMarketConfig() {
      if (loading_getMarketConfig) return;
      loading_getMarketConfig = true;

      const { version, symbols } = Storage.get("symbols") || {};
      this.symbols = symbols;

      get_symbol({
        params: {
          version,
        },
      })
        .then((data) => {
          if (data.symbols && data.version !== version) {
            console.log("【getSymbol update】", data);
            this.symbols = data.symbols;
            Storage.set("symbols", data);
          }
        })
        .catch(() => {})
        .finally(() => {
          loading_getMarketConfig = false;
        });
    }, //获取所有市场
    getCfdMarketConfig() {
      if (loading_getCfdMarketConfig) return;
      loading_getCfdMarketConfig = true;

      // 使用和现货一样的 Storage 方式读取缓存
      const { version, symbols } = Storage.get("cfdSymbols") || {};
      this.symbols = symbols;

      // 使用现货的 symbol 接口，添加 types: 'cfd' 过滤
      get_symbol({
        params: {
          version,
          types: 'cfd'
        },
      })
        .then((data) => {
          if (data.symbols && data.version !== version) {
            console.log("【getCfdSymbol update】", data);
            this.symbols = data.symbols;
            Storage.set("cfdSymbols", data);
          }
        })
        .catch(() => {})
        .finally(() => {
          loading_getCfdMarketConfig = false;
        });
    }, //获取所有CFD市场
    getLeverMarketConfig(finallyFun?: () => void) {
      get_marketConfig()
        .then((data: StateProps["leverConfigAry"]) => {
          this.leverConfigAry = data;
        })
        .finally(finallyFun);
    }, //获取杠杆所有市场
    getFapiSymbolList() {
      get_fapiSymbolList().then((data) => {
        this.futuresUsdtConfigAry = data;
      });
    }, //获取U本位合约市场列表
    getEtfByTradeMarket() {
      post_getEtfByTradeMarket({ data: { pairs: [] } }).then((data) => {
        const etfConfig = {};
        data.map(({ marketName, data }) => {
          etfConfig[marketName] = { data };
        });
        this.etfConfig = etfConfig;
      });
    }, //获取现货市场对应的etf所有市场
    getMarketTips() {
      get_marketTips().then((data) => {
        const obj = {};
        data.map((doc) => {
          obj[doc.marketName] = doc;
        });
        this.tips = obj;
      });
    }, //获取市场风险提示信息
    getPlateList() {
      get_plate().then((data) => {
        this.plateList = data;
      });
    }, //获取板块列表
    isEtfSymbolFn(symbolCfg: SymbolProps) {
      if (symbolCfg.plates && symbolCfg.plates.includes(145)) return true;
      return false;
    }, //判断是否是etf市场
    isMarketOpenFn(symbolCfg: ObjAny | SymbolProps, { server, local }): boolean {
      const { state, nextState, nextStateTime } = symbolCfg;
      if (state === "ONLINE") return true;
      if (state === "OFFLINE" && nextState === "ONLINE") {
        if (!nextStateTime || !server) return true;
        return server + (Date.now() - local) - nextStateTime > 0;
      }
      return true;
    }, //判断当前市场，是否已开盘的函数
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default market;
